import {PathLike} from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Route Trie
 *
 * A live representation of all indexed routes within
 * the Tapestry app. More than just a visual representation,
 * this Trie allow you to preload any route's JS and CSS
 * assets simply by calling:
 *
 * ```typescript
 * void RouteTrie.preloadPath('/path/to/something');
 * ```
 */
export class RouteTrie {
  private readonly trie = new RouteTrieNode('');

  public indexPath(path: PathLike, preloader?: () => Promise<any>) {
    const segments = this.toPathArray(path);
    let current: RouteTrieNode = this.trie;
    for (const segment of segments) {
      if (!current.has(segment)) {
        current.set(segment, new RouteTrieNode(segment));
      }
      current = current.get(segment);
    }
    if (current && preloader) {
      current.register(preloader);
    }
  }

  public preloadPath(path: PathLike) {
    const segments = this.toPathArray(path);
    let current: RouteTrieNode = this.trie;
    for (const segment of segments) {
      if (!current.has(segment)) {
        return;
      }
      current = current.get(segment);
    }
    return Promise.all(current.preloaders.map((preloader) => preloader()));
  }

  private toPathArray(path: PathLike) {
    if (typeof path === 'string') {
      return this.filterTokens(path.split('/'));
    }
    return this.filterTokens(path.flatMap((p) => p.split('/')));
  }

  private filterTokens(tokens: string[]) {
    return tokens.filter((t) => !!t);
  }
}

/**
 * Route Trie Node
 *
 * A node in the Route Trie. Each node holds a specific
 * path segment and a preload function.
 */
export class RouteTrieNode {
  public preloaders: (() => Promise<any>)[] = [];
  public readonly dictionary: Record<string, RouteTrieNode> = {};
  constructor(public readonly segment: string) {}

  public has(segment: string) {
    return segment in this.dictionary;
  }

  public get(segment: string) {
    return this.dictionary[segment];
  }

  public set(segment: string, node: RouteTrieNode) {
    this.dictionary[segment] = node;
  }

  public register(preloader: () => Promise<any>) {
    this.preloaders.push(preloader);
  }
}
